import React from 'react';
import { fmtCount, fmtFloat, fmtPct } from '../../metrics';
import {
  OdMetrics,
  prCorrectSureRate,
  prIncorrectSureRate,
  PrMetrics,
  prTop1Accuracy,
  RunResult,
  RunType,
} from '../../types';

interface MetricsSummaryProps {
  result: RunResult | null;
  runType: RunType;
}

function MetricCard({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="eval-metric-card">
      <div className="eval-metric-label">{label}</div>
      <div className="eval-metric-value">{value}</div>
      {sub && <div className="eval-metric-sub">{sub}</div>}
    </div>
  );
}

function ClassBox({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div
      style={{
        border: '1px solid var(--border)',
        borderRadius: 6,
        padding: '10px 12px',
        background: 'var(--surface-raised)',
      }}
    >
      <div
        style={{
          fontSize: 11,
          fontWeight: 600,
          color: 'var(--text-secondary)',
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          marginBottom: 10,
        }}
      >
        {label}
      </div>
      <div className="eval-metrics-grid" style={{ margin: 0 }}>
        {children}
      </div>
    </div>
  );
}

function OdClassBox({ od, label }: { od: OdMetrics | undefined; label: string }) {
  return (
    <ClassBox label={label}>
      <MetricCard label="Objects" value={fmtCount(od?.total_objects)} sub="count" />
      <MetricCard label="Precision" value={fmtPct(od?.precision)} sub="precision" />
      <MetricCard label="Recall" value={fmtPct(od?.recall)} sub="recall" />
      <MetricCard label="F1" value={fmtPct(od?.f1)} sub="f1" />
    </ClassBox>
  );
}

function PrClassBox({ pr, label }: { pr: PrMetrics | undefined; label: string }) {
  const total = pr?.total_objects ?? 0;
  const top3Rate = total > 0 && pr ? pr.correct_top3_cnt / total : null;
  const incUnsureRate = total > 0 && pr ? pr.incorrect_top1_unsure_cnt / total : null;

  return (
    <ClassBox label={label}>
      <MetricCard label="Objects" value={fmtCount(total)} sub="count" />
      <MetricCard
        label="Correct Sure"
        value={fmtPct(prCorrectSureRate(pr))}
        sub="correct_sure / total"
      />
      <MetricCard label="Top1 Acc" value={fmtPct(prTop1Accuracy(pr))} sub="correct / total" />
      <MetricCard label="Top3 Acc" value={fmtPct(top3Rate)} sub="correct_top3 / total" />
      <MetricCard
        label="Incorr Sure"
        value={fmtPct(prIncorrectSureRate(pr))}
        sub="incorr_sure / total"
      />
      <MetricCard label="Incorr Unsure" value={fmtPct(incUnsureRate)} sub="incorr_unsure / total" />
      <MetricCard label="Avg Exp Conf" value={fmtFloat(pr?.avg_exp_confidence)} sub="confidence" />
      <MetricCard
        label="Avg Top1 Conf"
        value={fmtFloat(pr?.avg_top1_confidence)}
        sub="confidence"
      />
      <MetricCard
        label="Avg Top3 Conf"
        value={fmtFloat(pr?.avg_top3_confidence)}
        sub="confidence"
      />
      <MetricCard
        label="Rec Sim Thr"
        value={fmtFloat(pr?.recommended_similarity_thr)}
        sub="recommended"
      />
      <MetricCard
        label="Rec Delta Thr"
        value={fmtFloat(pr?.recommended_delta_thr)}
        sub="recommended"
      />
    </ClassBox>
  );
}

export const MetricsSummary: React.FC<MetricsSummaryProps> = ({ result, runType }) => {
  const od = result?.OD;
  const pr = result?.PR;
  const pc = result?.PC;

  const showOD = runType === 'OD' || runType === 'Full';
  const showPR = runType === 'PR' || runType === 'Full';
  const showPC = runType === 'PC' || runType === 'Full';

  return (
    <div className="eval-metrics-grid">
      {showOD && (
        <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 180px' }}>
            <OdClassBox od={od?.products} label="Products" />
          </div>
          <div style={{ flex: '1 1 180px' }}>
            <OdClassBox od={od?.cartons} label="Cartons" />
          </div>
          <div style={{ flex: '1 1 180px' }}>
            <OdClassBox od={od?.shelves} label="Shelves" />
          </div>
          <div style={{ flex: '1 1 180px' }}>
            <OdClassBox od={od?.mods} label="Mods" />
          </div>
          <div style={{ flex: '1 1 180px' }}>
            <OdClassBox od={od?.template_boxes} label="Template Boxes" />
          </div>
          <div style={{ flex: '1 1 180px' }}>
            <OdClassBox od={od?.price_tags} label="Price Tags" />
          </div>
        </div>
      )}
      {showPR && (
        <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 340px' }}>
            <PrClassBox pr={pr?.products} label="Products" />
          </div>
          <div style={{ flex: '1 1 340px' }}>
            <PrClassBox pr={pr?.cartons} label="Cartons" />
          </div>
        </div>
      )}
      {showPC && (
        <>
          <MetricCard
            label="POG Compliant F1"
            value={fmtPct(pc?.pog?.compliant?.f1)}
            sub="planogram"
          />
          <MetricCard
            label="POG Non-Compliant F1"
            value={fmtPct(pc?.pog?.non_compliant?.f1)}
            sub="planogram"
          />
          <MetricCard label="POG Out F1" value={fmtPct(pc?.pog?.out?.f1)} sub="planogram" />
          <MetricCard
            label="POG Faceover F1"
            value={fmtPct(pc?.pog?.faceover?.f1)}
            sub="planogram"
          />
          <MetricCard label="ROG Correct F1" value={fmtPct(pc?.rog?.correct?.f1)} sub="realogram" />
          <MetricCard label="ROG Move F1" value={fmtPct(pc?.rog?.move?.f1)} sub="realogram" />
          <MetricCard label="ROG Remove F1" value={fmtPct(pc?.rog?.remove?.f1)} sub="realogram" />
          <MetricCard label="ROG Out F1" value={fmtPct(pc?.rog?.out?.f1)} sub="realogram" />
        </>
      )}
    </div>
  );
};
